import { inject, Injectable } from '@angular/core';

import Echo from 'laravel-echo';

import { defer, Observable, Subject } from 'rxjs';

import { environment } from '@qwyk/portals/environment';
import {
    NotificationsFacade,
    NotificationsEntity
} from '@qwyk/portals/data-access/notifications';

@Injectable({
    providedIn: 'root',
})
export class PortalsSocketNotificationsService {

    private readonly notificationsFacade: NotificationsFacade = inject(NotificationsFacade);

    private initialized = false;
    private connected = false;
    private userId: string | null = null; // Store userId
    private token: string | null = null; // Store token
    private privateChannel = null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private echo!: Echo<any>;

    public registerEchoListener(
        token: string,
        userId: string
    ): Observable<NotificationsEntity> {
        if (this.initialized) {
            this.deregisterEchoListener();
        }

        const value = new Subject<NotificationsEntity>();
        const observable = value.asObservable();

        this.userId = userId; // Store userId
        this.token = token; // Store token

        this.privateChannel = `${environment.echoConfig.userModel}.${this.userId}`;
        this.echo = new Echo({
            broadcaster: 'pusher',
            key: environment.echoConfig.options.key,
            cluster: environment.echoConfig.options.cluster,
            authEndpoint: environment.echoConfig.options.authEndpoint,
            auth: {
                headers: {
                    Accept: 'application/json',
                    Authorization: `Bearer ${token}`
                }
            },
            forceTLS: environment.echoConfig.options.forceTLS,
            disableStats: true,
            enabledTransports: environment.echoConfig.options.enabledTransports
        });

        const observer = defer(async () => {
            await this.echo.private(this.privateChannel).notification(notification => {
                value.next(notification);
            });
            this.initialized = true;
        });

        observer.subscribe();

        this.subscribeEchoConnectionState();
        return observable;
    }

    public deregisterEchoListener() {
        if (!this.echo) {
            return;
        }

        this.initialized = false;
        this.echo.leave(
            `private-${environment.echoConfig.userModel}.${this.userId}`
        );
        this.echo.disconnect();
        this.connected = false;
        this.userId = null; // Clear userId on deregister
        this.token = null; // Clear token on deregister
    }

    private subscribeEchoConnectionState() {
        this.echo.connector.pusher.connection.bind('disconnected', (state) => {
            if (!state && this.initialized && this.connected && this.token && this.userId) {
                this.notificationsFacade.reinitializeEchoListener(this.token, this.userId);
            }
        });
    }
}
